/**
 * Protein Calculator - Custom Element
 *
 * Handles:
 * - Range slider sync (value <-> output display)
 * - Form validation
 * - Protein calculation (basisfactor * activiteit * leeftijd * gewicht)
 * - Klaviyo event tracking for email delivery
 */

if (!customElements.get('protein-calculator')) {
  class ProteinCalculator extends HTMLElement {
    constructor() {
      super();

      /* ── Calculation config (placeholder values - update with real GF formulas) ── */
      this.CONFIG = {
        basisfactor: {
          MASSA: 1.8,
          CUT: 1.6,
          BEHOUD: 0.83
        },
        activiteitscorrectie: {
          INACTIEF: 1.0,
          LICHT: 1.1,
          ACTIEF: 1.2,
          ZEER: 1.3
        },
        leeftijdscorrectie: [
          { maxAge: 39, factor: 1.0 },
          { maxAge: 65, factor: 1.1 },
          { maxAge: Infinity, factor: 1.2 }
        ]
      };
    }

    connectedCallback() {
      this.form = this.querySelector('.pc__form');
      this.resultSection = this.querySelector('[data-pc-result]');
      this.validationBox = this.querySelector('[data-pc-validation]');
      this.validationText = this.validationBox?.querySelector('.pc-validation-message__text');
      this.submitBtn = this.querySelector('.pc-submit-wrap .btn');
      this.submitBtnText = this.querySelector('.pc-submit-btn__text');
      this.submitBtnLoader = this.querySelector('.pc-submit-wrap .loading__spinner');
      this.recalcBtn = this.querySelector('[data-pc-recalc]');

      this.initSliders();
      this.bindEvents();
    }

    /* ══════════════════════════════════════════════
       SLIDERS
       ══════════════════════════════════════════════ */

    initSliders() {
      this.sliders = this.querySelectorAll('[data-pc-slider]');
      this.sliders.forEach((sliderWrap) => {
        const range = sliderWrap.querySelector('input[type="range"]');
        const output = sliderWrap.querySelector('[data-pc-output]');

        if (!range || !output) return;

        const handleSync = () => {
          output.textContent = range.value;
          range.setAttribute('aria-valuenow', range.value);

          // Update filled track
          const min = parseFloat(range.min);
          const max = parseFloat(range.max);
          const pct = ((range.value - min) / (max - min)) * 100;
          range.style.setProperty('--pc-fill', pct + '%');
        };

        range.addEventListener('input', handleSync);
        range.addEventListener('change', handleSync);

        // Set initial value
        handleSync();
      });
    }

    /* ══════════════════════════════════════════════
       EVENTS
       ══════════════════════════════════════════════ */

    bindEvents() {
      this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
      this.recalcBtn?.addEventListener('click', () => this.handleRecalculate());

      // Clear validation on input change
      this.form?.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', () => this.hideValidation());
        input.addEventListener('input', () => this.hideValidation());
      });
    }

    handleSubmit(e) {
      e.preventDefault();

      const data = this.collectFormData();
      const error = this.validate(data);

      if (error) {
        this.showValidation(error);
        return;
      }

      this.setLoading(true);

      // Calculate
      const result = this.calculate(data);

      // Send to Klaviyo
      this.sendToKlaviyo(data, result);

      // Show result (small delay for perceived processing)
      setTimeout(() => {
        this.showResult(data, result);
        this.setLoading(false);
      }, 600);
    }

    handleRecalculate() {
      this.resultSection.hidden = true;
      this.form.hidden = false;
      this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ══════════════════════════════════════════════
       DATA COLLECTION
       ══════════════════════════════════════════════ */

    collectFormData() {
      const getVal = (name) => {
        const el = this.form.querySelector(`[name="${name}"]`);
        if (!el) return '';
        if (el.type === 'radio') {
          const checked = this.form.querySelector(`[name="${name}"]:checked`);
          return checked ? checked.value : '';
        }
        return el.value;
      };

      const getRange = (name) => {
        const slider = this.querySelector(`[data-pc-slider="${name}"] input[type="range"]`);
        return slider ? parseInt(slider.value, 10) : 0;
      };

      return {
        age: getRange('age'),
        weight: getRange('weight'),
        height: parseInt(getVal('height'), 10) || 0,
        activity: getVal('activity'),
        goal: getVal('goal'),
        name: getVal('name').trim(),
        email: getVal('email').trim()
      };
    }

    /* ══════════════════════════════════════════════
       VALIDATION
       ══════════════════════════════════════════════ */

    validate(data) {
      if (!data.height || data.height < 120 || data.height > 220) {
        return 'Vul je lengte in (tussen 120 en 220 cm).';
      }
      if (!data.activity) {
        return 'Selecteer je activiteitsniveau.';
      }
      if (!data.goal) {
        return 'Selecteer je doel.';
      }
      if (!data.name) {
        return 'Vul je naam in.';
      }
      if (!data.email || !this.isValidEmail(data.email)) {
        return 'Vul een geldig e-mailadres in.';
      }
      return null;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    showValidation(message) {
      if (!this.validationBox || !this.validationText) return;
      this.validationText.textContent = message;
      this.validationBox.hidden = false;
      this.validationBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    hideValidation() {
      if (this.validationBox) {
        this.validationBox.hidden = true;
      }
    }

    /* ══════════════════════════════════════════════
       CALCULATION
       ══════════════════════════════════════════════ */

    calculate(data) {
      const basisfactor = this.CONFIG.basisfactor[data.goal] || 0.83;
      const activiteitscorrectie = this.CONFIG.activiteitscorrectie[data.activity] || 1.0;

      let leeftijdscorrectie = 1.0;
      for (const range of this.CONFIG.leeftijdscorrectie) {
        if (data.age <= range.maxAge) {
          leeftijdscorrectie = range.factor;
          break;
        }
      }

      const eiwitPerDag = Math.round(data.weight * basisfactor * activiteitscorrectie * leeftijdscorrectie);
      const perMaaltijd = Math.round(eiwitPerDag / 4);

      return {
        eiwitPerDag,
        perMaaltijd,
        basisfactor,
        activiteitscorrectie,
        leeftijdscorrectie
      };
    }

    /* ══════════════════════════════════════════════
       KLAVIYO INTEGRATION
       ══════════════════════════════════════════════ */

    sendToKlaviyo(data, result) {
      const companyId = this.getAttribute('data-klaviyo-key');
      if (!companyId) {
        console.warn('Klaviyo: geen public key ingesteld');
        return;
      }

      // Collect product data from data-attributes for Klaviyo email
      const products = [];
      for (let i = 1; i <= 2; i++) {
        const title = this.getAttribute(`data-product-${i}-title`);
        if (title) {
          products.push({
            title,
            url: this.getAttribute(`data-product-${i}-url`) || '',
            image: this.getAttribute(`data-product-${i}-image`) || '',
            price: this.getAttribute(`data-product-${i}-price`) || ''
          });
        }
      }

      const eventData = {
        naam: data.name,
        email: data.email,
        leeftijd: data.age,
        gewicht: data.weight,
        lengte: data.height,
        activiteit: this.getActivityLabel(data.activity),
        activiteit_code: data.activity,
        doel: this.getGoalLabel(data.goal),
        doel_code: data.goal,
        eiwit_per_dag: result.eiwitPerDag,
        per_maaltijd: result.perMaaltijd,
        basisfactor: result.basisfactor,
        activiteitscorrectie: result.activiteitscorrectie,
        leeftijdscorrectie: result.leeftijdscorrectie,
        producten: products
      };

      // Use Klaviyo Client API directly (bypasses cookie consent issues)
      const url = `https://a.klaviyo.com/client/events/?company_id=${companyId}`;

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: 'Eiwit Calculator Resultaat' }
                }
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: data.email,
                    first_name: data.name
                  }
                }
              },
              properties: eventData
            }
          }
        })
      })
        .then((res) => {
          if (res.ok) {
            console.log('Klaviyo event verstuurd');
          } else {
            console.warn('Klaviyo API fout:', res.status);
          }
        })
        .catch((err) => console.warn('Klaviyo tracking failed:', err));
    }

    getActivityLabel(code) {
      const labels = {
        INACTIEF: 'Inactief',
        LICHT: 'Licht actief',
        ACTIEF: 'Actief',
        ZEER: 'Zeer actief'
      };
      return labels[code] || code;
    }

    getGoalLabel(code) {
      const labels = {
        MASSA: 'Spieropbouw',
        CUT: 'Gewicht verliezen',
        BEHOUD: 'Gewicht behoud'
      };
      return labels[code] || code;
    }

    /* ══════════════════════════════════════════════
       RESULT DISPLAY
       ══════════════════════════════════════════════ */

    showResult(data, result) {
      // Fill in result values
      const nameEl = this.querySelector('[data-pc-result-name]');
      const dailyEl = this.querySelector('[data-pc-result-daily]');
      const mealEl = this.querySelector('[data-pc-result-meal]');

      if (nameEl) nameEl.textContent = data.name;
      if (dailyEl) dailyEl.textContent = result.eiwitPerDag;
      if (mealEl) mealEl.textContent = result.perMaaltijd;

      // Populate summary table
      const goalEl = this.querySelector('[data-pc-result-goal]');
      const activityEl = this.querySelector('[data-pc-result-activity]');
      const ageEl = this.querySelector('[data-pc-result-age]');
      const weightEl = this.querySelector('[data-pc-result-weight]');
      const heightEl = this.querySelector('[data-pc-result-height]');

      if (goalEl) goalEl.textContent = this.getGoalLabel(data.goal);
      if (activityEl) activityEl.textContent = this.getActivityLabel(data.activity);
      if (ageEl) ageEl.textContent = data.age + ' jaar';
      if (weightEl) weightEl.textContent = data.weight + ' kg';
      if (heightEl) heightEl.textContent = data.height + ' cm';

      // Hide form, show result
      this.form.hidden = true;
      this.hideValidation();
      this.resultSection.hidden = false;

      // Scroll to result
      this.resultSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ══════════════════════════════════════════════
       LOADING STATE
       ══════════════════════════════════════════════ */

    setLoading(isLoading) {
      if (this.submitBtn) {
        this.submitBtn.disabled = isLoading;
        this.submitBtn.classList.toggle('btn--loading', isLoading);
      }
      if (this.submitBtnText) {
        this.submitBtnText.hidden = isLoading;
      }
      if (this.submitBtnLoader) {
        this.submitBtnLoader.classList.toggle('hidden', !isLoading);
      }
    }
  }

  customElements.define('protein-calculator', ProteinCalculator);
}
